// Generic placeholder page for nav sections not yet built.
// Shows a designed empty state with domain icon, page name, description, and phase label.
import type { JSX } from 'react';
import {
  CheckSquare,
  Package,
  ShoppingCart,
  Boxes,
  Tag,
  Truck,
  RotateCcw,
  ArrowLeftRight,
  DollarSign,
  BarChart2,
  Building2,
  Settings,
  Construction,
  type LucideIcon,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

interface RouteInfo {
  domain: string;
  sub?: string;
  desc: string;
  phase: string;
}

const DOMAIN_ICONS: Record<string, LucideIcon> = {
  Tasks: CheckSquare,
  Catalog: Package,
  Purchasing: ShoppingCart,
  Inventory: Boxes,
  Listings: Tag,
  Fulfillment: Truck,
  Returns: RotateCcw,
  Transfers: ArrowLeftRight,
  Finance: DollarSign,
  Reports: BarChart2,
  Directory: Building2,
  Settings: Settings,
};

const ROUTE_LABELS: Record<string, RouteInfo> = {
  '/tasks': {
    domain: 'Tasks',
    desc: 'View and manage all tasks across domains. Role-filtered, with deep-link action buttons.',
    phase: 'Phase 2B',
  },
  '/catalog/products': {
    domain: 'Catalog',
    sub: 'Products',
    desc: 'Master product database with 100K+ row support.',
    phase: 'Phase 1',
  },
  '/catalog/flagged': {
    domain: 'Catalog',
    sub: 'Flagged',
    desc: 'Products flagged for quality review.',
    phase: 'Phase 1',
  },
  '/purchasing': {
    domain: 'Purchasing',
    sub: 'Purchasing Pipeline',
    desc: 'Overview of all purchasing activity across stages.',
    phase: 'Phase 2A',
  },
  '/purchasing/po-builder': {
    domain: 'Purchasing',
    sub: 'PO Builder',
    desc: 'Build and submit purchase orders.',
    phase: 'Phase 1',
  },
  '/purchasing/purchase-orders': {
    domain: 'Purchasing',
    sub: 'Purchase Orders',
    desc: 'View and manage all purchase orders.',
    phase: 'Phase 1',
  },
  '/purchasing/receiving': {
    domain: 'Purchasing',
    sub: 'Receiving',
    desc: 'Receive inventory against purchase orders. Scan, count, and confirm deliveries.',
    phase: 'Phase 3',
  },
  '/purchasing/discrepancies': {
    domain: 'Purchasing',
    sub: 'Discrepancies',
    desc: 'Review and resolve purchasing discrepancies between ordered and received quantities.',
    phase: 'Phase 3',
  },
  '/inventory': {
    domain: 'Inventory',
    sub: 'Stock Visibility',
    desc: 'Unified view of all inventory across locations: warehouse, FBA, FBM, and in-transit.',
    phase: 'Phase 2C',
  },
  '/inventory/reconciliation': {
    domain: 'Inventory',
    sub: 'Reconciliation',
    desc: 'Reconcile physical counts against system records.',
    phase: 'Post-launch',
  },
  '/listings': {
    domain: 'Listings',
    sub: 'Listings Overview',
    desc: 'Overview of all listings across channels.',
    phase: 'Phase 2C',
  },
  '/listings/review': {
    domain: 'Listings',
    sub: 'Review',
    desc: 'Review and approve listing content before publishing.',
    phase: 'Post-launch',
  },
  '/fulfillment': {
    domain: 'Fulfillment',
    sub: 'Fulfillment Pipeline',
    desc: 'End-to-end order fulfillment pipeline from order to delivery.',
    phase: 'Phase 3',
  },
  '/fulfillment/orders': {
    domain: 'Fulfillment',
    sub: 'Orders',
    desc: 'Incoming orders awaiting processing.',
    phase: 'Phase 3',
  },
  '/fulfillment/channel-fulfilled': {
    domain: 'Fulfillment',
    sub: 'Channel Fulfilled',
    desc: 'Orders fulfilled directly by the channel.',
    phase: 'Phase 3',
  },
  '/fulfillment/review': {
    domain: 'Fulfillment',
    sub: 'Review',
    desc: 'Orders requiring manual review before fulfillment.',
    phase: 'Phase 3',
  },
  '/fulfillment/verify': {
    domain: 'Fulfillment',
    sub: 'Verify',
    desc: 'Verify order details and inventory availability.',
    phase: 'Phase 3',
  },
  '/fulfillment/fulfill': {
    domain: 'Fulfillment',
    sub: 'Fulfill',
    desc: 'Orders ready to be fulfilled.',
    phase: 'Phase 3',
  },
  '/fulfillment/pick': {
    domain: 'Fulfillment',
    sub: 'Pick',
    desc: 'Pick lists for warehouse staff.',
    phase: 'Phase 3',
  },
  '/fulfillment/ship': {
    domain: 'Fulfillment',
    sub: 'Ship',
    desc: 'Orders packed and ready for shipping.',
    phase: 'Phase 3',
  },
  '/fulfillment/awaiting-label': {
    domain: 'Fulfillment',
    sub: 'Awaiting Label',
    desc: 'Orders waiting for shipping labels.',
    phase: 'Phase 3',
  },
  '/fulfillment/awaiting-pickup': {
    domain: 'Fulfillment',
    sub: 'Awaiting Pickup',
    desc: 'Labeled orders awaiting carrier pickup.',
    phase: 'Phase 3',
  },
  '/fulfillment/pto': {
    domain: 'Fulfillment',
    sub: 'PTO',
    desc: 'Prep-to-order queue for FBA inbound shipments.',
    phase: 'Phase 3',
  },
  '/fulfillment/on-hold': {
    domain: 'Fulfillment',
    sub: 'On Hold',
    desc: 'Orders on hold pending resolution.',
    phase: 'Phase 3',
  },
  '/fulfillment/followups': {
    domain: 'Fulfillment',
    sub: 'Followups',
    desc: 'Orders requiring follow-up action.',
    phase: 'Phase 3',
  },
  '/fulfillment/discrepancies': {
    domain: 'Fulfillment',
    sub: 'Discrepancies',
    desc: 'Fulfillment discrepancies to investigate.',
    phase: 'Phase 3',
  },
  '/returns': {
    domain: 'Returns',
    sub: 'Returns Pipeline',
    desc: 'End-to-end returns processing from receipt to disposition.',
    phase: 'Phase 3',
  },
  '/returns/inspection': {
    domain: 'Returns',
    sub: 'Inspection',
    desc: 'Inspect returned items and assess condition.',
    phase: 'Phase 3',
  },
  '/returns/disposition': {
    domain: 'Returns',
    sub: 'Disposition',
    desc: 'Decide return outcome: relist, return to distributor, or dispose.',
    phase: 'Phase 3',
  },
  '/returns/discrepancies': {
    domain: 'Returns',
    sub: 'Discrepancies',
    desc: 'Returns discrepancies to resolve.',
    phase: 'Phase 3',
  },
  '/transfers/inbound': {
    domain: 'Transfers',
    sub: 'Inbound',
    desc: 'Manage FBA inbound shipments and prep workflows.',
    phase: 'Phase 3',
  },
  '/transfers/removals': {
    domain: 'Transfers',
    sub: 'Removals',
    desc: 'Track FBA removal orders and returns to warehouse.',
    phase: 'Phase 3',
  },
  '/finance/credit-cash': {
    domain: 'Finance',
    sub: 'Credit & Cash',
    desc: 'Monitor credit limits, cash balances, and soft reserves across distributors.',
    phase: 'Phase 2B',
  },
  '/finance/invoices': {
    domain: 'Finance',
    sub: 'Invoices',
    desc: 'Track distributor invoices and match to purchase orders.',
    phase: 'Phase 2B',
  },
  '/finance/payouts': {
    domain: 'Finance',
    sub: 'Payouts',
    desc: 'Marketplace payout tracking and reconciliation.',
    phase: 'Phase 2C',
  },
  '/finance/reconciliation': {
    domain: 'Finance',
    sub: 'Reconciliation',
    desc: 'Financial reconciliation across all accounts.',
    phase: 'Phase 3',
  },
  '/finance/expenses': {
    domain: 'Finance',
    sub: 'Expenses',
    desc: 'Track operational expenses: shipping, prep, storage, subscriptions.',
    phase: 'Phase 3',
  },
  '/finance/payroll': {
    domain: 'Finance',
    sub: 'Payroll',
    desc: 'Payroll tracking for VA and warehouse staff.',
    phase: 'Phase 3',
  },
  '/finance/review': {
    domain: 'Finance',
    sub: 'Review',
    desc: 'Financial review queue for admin approval.',
    phase: 'Phase 3',
  },
  '/reports': {
    domain: 'Reports',
    desc: 'Profitability, inventory, purchasing, operations, and custom reports.',
    phase: 'Phase 2C',
  },
  '/directory/distributors': {
    domain: 'Directory',
    sub: 'Distributors',
    desc: 'Manage distributor profiles, credit terms, and contact information.',
    phase: 'Phase 2A',
  },
  '/directory/brands': {
    domain: 'Directory',
    sub: 'Brands',
    desc: 'Brand registry for categorization and restriction management.',
    phase: 'Phase 2A',
  },
  '/directory/categories': {
    domain: 'Directory',
    sub: 'Categories',
    desc: 'Product category taxonomy and fee mappings.',
    phase: 'Phase 2A',
  },
  '/directory/marketplaces': {
    domain: 'Directory',
    sub: 'Marketplaces',
    desc: 'Marketplace configuration: fees, API keys, and fulfillment rules.',
    phase: 'Phase 2A',
  },
};

export default function Placeholder(): JSX.Element {
  const { pathname } = useLocation();
  const info: RouteInfo | undefined = ROUTE_LABELS[pathname];

  const title = info ? info.sub || info.domain : pathname;

  const domain = info?.domain || 'Unknown';
  const Icon: LucideIcon = DOMAIN_ICONS[domain] || Construction;
  const desc = info?.desc || 'This page will be built on the scheduled build day.';
  const phase = info?.phase || 'Phase 3';

  // Distributor-selector visibility RETIRED — U10 SLICE B (20260719000045):
  // domain_scope_config dropped (fork 3). At 0 live rows (the state since the
  // U1 wipe) isScopedRoute was already fail-safe FALSE everywhere — placeholder
  // pages rendered NO selector; that live behavior is now static.

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-semibold text-ink-bright">{title}</h1>
        </div>
      </div>

      {/* Empty state */}
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8">
        <div className="w-16 h-16 rounded-xl bg-surface-2 border border-border flex items-center justify-center">
          <Icon size={32} className="text-ink-muted" />
        </div>
        <div className="max-w-sm">
          <h2 className="text-base font-semibold text-ink-bright">{title}</h2>
          <p className="text-xs text-ink-muted mt-2 leading-relaxed">{desc}</p>
          <span className="inline-block mt-3 px-2 py-0.5 rounded text-2xs font-medium bg-accent/10 text-accent">
            {phase === 'Post-launch' ? 'Post-launch' : `Coming in ${phase}`}
          </span>
        </div>
      </div>
    </div>
  );
}
